import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { isValidNumber } from '../../../utils/math/number';
import { isDefined } from '../../../utils/variables';
import { Point2d } from '../../../viewer/utilities/math-helpers';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { ProgressController } from '../../utils/progress-controller';
import { Button } from '../button/button';
import { Dialog } from '../dialog/dialog';
import { ProgressBar } from '../progress-bar/progress-bar';
import { TextBox } from '../text-box/text-box';

interface Props {
  onConfirm: (value: string) => Promise<void> | void;
  onCancel: () => Promise<void> | void;
  title: string;
  position?: Point2d;
  width?: number;
  initialValue?: string;
  valueType?: 'text' | 'number';
  children?: React.ReactNode;
  disabled?: boolean;
  progressController?: ProgressController;
}

/**
 * A simple dialog with a text field that allows inputting a value and confirming
 */
const InputDialog = (props: Props) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = React.useState(props.initialValue ?? '');
  const [loading, setLoading] = React.useState(false);
  const { handleError, buildErrorList } = useErrorHandling({ displayNonApiErrors: true });
  const [progress, setProgress] = React.useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const [progressText, setProgressText] = React.useState<string>(t('preparing', { ns: 'common' }));

  const onProgress = (amount: number, text?: string) => {
    setProgress(amount);
    if (text) {
      setProgressText(text);
    }
  };

  React.useEffect(() => {
    if (props.progressController) {
      props.progressController.callback = onProgress;
    }
  }, [props]);

  const onConfirm = async () => {
    try {
      setLoading(true);
      await props.onConfirm(inputValue.trim());
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isInputValid(inputValue)) {
      e.stopPropagation();
      await onConfirm();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      await props.onCancel();
    }
  };

  const isInputValid = (input: string) => {
    if (!isDefined(input)) {
      return false;
    }

    if (props.valueType === 'number') {
      return isValidNumber(Number(input));
    }

    return input.trim().length > 0;
  };

  return (
    <Dialog
      closeIcon={false}
      closeOnDimmerClick={false}
      position={props.position ? { left: props.position.x, top: props.position.y } : 'center'}
      width={props.width ?? 250}
      onClose={props.onCancel}
    >
      {{
        header: props.title,
        content: (
          <Content>
            {props.children}
            <TextBox
              autoFocus={true}
              disabled={props.disabled ?? false}
              hideStepper={true}
              type={props.valueType ?? 'text'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={onKeyDown}
            />

            {buildErrorList()}
          </Content>
        ),
        footer: {
          right: (
            <>
              {loading && isDefined(props.progressController) && (
                <ProgressBar height="30px" max={1} min={0} text={progressText} value={progress} />
              )}
              <Button
                color="primary"
                disabled={loading || !isInputValid(inputValue)}
                loading={loading}
                variant="contained"
                onClick={onConfirm}
              >
                {t('confirm', { ns: 'common' })}
              </Button>
              <Button disabled={loading} variant="text" onClick={props.onCancel}>
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

export { InputDialog };
